import api from './api';
import { IFuncionario, FuncionarioCreationAttributes, ApiResponse } from '../types';
import { env } from '../utils/env';

// Configuração base da API
const API_BASE_URL = env.API_URL;

export interface FuncionarioFilters {
  search?: string;
  status?: string;
  cargo?: string;
  estado?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

export interface FuncionarioStats {
  total: number;
  porStatus: {
    ativos: number;
    inativos: number;
    demitidos: number;
    aposentados: number;
  };
  porCargo: Array<{
    cargo: string;
    total: number;
  }>;
  porEstado: Array<{
    estado: string;
    total: number;
  }>;
}

class FuncionarioService {
  // Listar funcionários com filtros
  async getFuncionarios(filters: FuncionarioFilters = {}): Promise<ApiResponse<IFuncionario[]>> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, value.toString());
      }
    });



    const response = await api.get(`/funcionarios?${params.toString()}`);
    return response as unknown as ApiResponse<IFuncionario[]>;
  }

  // Obter funcionário por ID
  async getFuncionarioById(id: number): Promise<ApiResponse<IFuncionario>> {
    const response = await api.get(`/funcionarios/${id}`);
    return response as unknown as ApiResponse<IFuncionario>;
  }

  // Obter funcionários ativos (para select)
  async getFuncionariosAtivos(): Promise<ApiResponse<IFuncionario[]>> {
    const response = await api.get('/funcionarios?status=ativo&limit=1000');
    return response as unknown as ApiResponse<IFuncionario[]>;
  }

  // Criar novo funcionário
  async createFuncionario(data: FuncionarioCreationAttributes): Promise<ApiResponse<IFuncionario>> {
    const response = await api.post('/funcionarios', data);
    return response as unknown as ApiResponse<IFuncionario>;
  }

  // Atualizar funcionário
  async updateFuncionario(id: number, data: Partial<FuncionarioCreationAttributes>): Promise<ApiResponse<IFuncionario>> {
    const response = await api.put(`/funcionarios/${id}`, data);
    return response as unknown as ApiResponse<IFuncionario>;
  }

  // Excluir funcionário
  async deleteFuncionario(id: number): Promise<ApiResponse<void>> {
    const response = await api.delete(`/funcionarios/${id}`);
    return response as unknown as ApiResponse<void>;
  }

  // Alterar status do funcionário
  async toggleStatus(id: number, status: 'ativo' | 'inativo' | 'demitido' | 'aposentado'): Promise<ApiResponse<IFuncionario>> {
    const response = await api.patch(`/funcionarios/${id}/status`, { status });
    return response as unknown as ApiResponse<IFuncionario>;
  }

  // Upload de foto
  async uploadFoto(id: number, file: File): Promise<ApiResponse<{ foto: string }>> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post(`/funcionarios/${id}/foto`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response as unknown as ApiResponse<{ foto: string }>;
  }

  // Obter estatísticas
  async getStats(): Promise<ApiResponse<FuncionarioStats>> {
    const response = await api.get('/funcionarios/stats/overview');
    return response as unknown as ApiResponse<FuncionarioStats>;
  }

  // Verificar disponibilidade de CPF
  async checkCpfAvailability(cpf: string, id?: number): Promise<ApiResponse<{ available: boolean; cpf: string }>> {
    const params = new URLSearchParams({ cpf });
    if (id) {
      params.append('id', id.toString());
    }

    const response = await api.get(`/funcionarios/check/cpf?${params.toString()}`);
    return response as unknown as ApiResponse<{ available: boolean; cpf: string }>;
  }

  // Verificar disponibilidade de email
  async checkEmailAvailability(email: string, id?: number): Promise<ApiResponse<{ available: boolean; email: string }>> {
    const params = new URLSearchParams({ email });
    if (id) {
      params.append('id', id.toString());
    }

    const response = await api.get(`/funcionarios/check/email?${params.toString()}`);
    return response as unknown as ApiResponse<{ available: boolean; email: string }>;
  }

  // Formatar CPF para exibição
  formatCpf(cpf: string): string {
    if (!cpf) return '';
    const cleanCpf = cpf.replace(/\D/g, '');
    return cleanCpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }

  // Formatar CEP para exibição
  formatCep(cep: string): string {
    if (!cep) return '';
    const cleanCep = cep.replace(/\D/g, '');
    return cleanCep.replace(/(\d{5})(\d{3})/, '$1-$2');
  }

  // Formatar telefone para exibição
  formatPhone(phone: string): string {
    if (!phone) return '';
    const cleanPhone = phone.replace(/\D/g, '');
    
    if (cleanPhone.length === 11) {
      return cleanPhone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else if (cleanPhone.length === 10) {
      return cleanPhone.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    
    return phone;
  }

  // Formatar salário para exibição
  formatSalary(salary: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(salary);
  }

  // Formatar data para exibição
  formatDate(date: string | Date): string {
    if (!date) return '';
    return new Date(date).toLocaleDateString('pt-BR');
  }

  // Obter status colorido
  getStatusColor(status: string): string {
    switch (status) {
      case 'ativo':
        return 'success';
      case 'inativo':
        return 'warning';
      case 'demitido':
        return 'error';
      case 'aposentado':
        return 'info';
      default:
        return 'default';
    }
  }

  // Obter tipo de contrato colorido
  getContractTypeColor(tipo: string): string {
    switch (tipo) {
      case 'CLT':
        return 'primary';
      case 'PJ':
        return 'success';
      case 'estagiario':
        return 'info';
      case 'temporario':
        return 'warning';
      default:
        return 'default';
    }
  }

  // Validar CPF
  validateCpf(cpf: string): boolean {
    const cleanCpf = cpf.replace(/\D/g, '');
    
    if (cleanCpf.length !== 11) return false;
    
    // Verificar se todos os dígitos são iguais
    if (/^(\d)\1{10}$/.test(cleanCpf)) return false;
    
    // Validar primeiro dígito verificador
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(cleanCpf.charAt(i)) * (10 - i);
    }
    let remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cleanCpf.charAt(9))) return false;
    
    // Validar segundo dígito verificador
    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += parseInt(cleanCpf.charAt(i)) * (11 - i);
    }
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cleanCpf.charAt(10))) return false;
    
    return true;
  }

  // Validar CEP
  validateCep(cep: string): boolean {
    const cleanCep = cep.replace(/\D/g, '');
    return cleanCep.length === 8;
  }

  // Validar email
  validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Obtém a URL completa do avatar
   */
  getAvatarUrl(avatarPath?: string): string {
    if (!avatarPath) {
      return '';
    }

    // Se já é uma URL completa, retorna como está
    if (avatarPath.startsWith('http')) {
      return avatarPath;
    }

    // Constrói a URL completa baseada no backend
    const baseUrl = API_BASE_URL;
    return `${baseUrl}/uploads/avatars/${avatarPath}`;
  }
}

export default new FuncionarioService(); 